import { useCallback, useRef, useState } from 'react'

import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import { isEnterKey } from '~/libs/keyboard'
import type { CellPos, CellRange } from '~/models/gridSelection'
import {
  isMultiCellRange,
  makeRange,
  parseTsv,
  planPaste,
  rangeCells,
  rangeToTsv,
  stepCell,
} from '~/models/gridSelection'
import type {
  CellValueParseResult,
  RawRejectionReport,
} from '~/pages/Supply48Data/models/cellValue'
import { NO_REJECTIONS } from '~/pages/Supply48Data/models/cellValue'
import type { CellEdit } from '~/pages/Supply48Data/models/editHistory'

/**
 * グリッド 1 セルの情報。row/col から引けるようにグリッド側が供給する。
 */
export type GridCellInfo = {
  legId: string
  date: ProtoDate
  koma: number
  /**
   * GC ロック外かつ書き込み可能 (閲覧モードでは常に false)。
   * 選択・フォーカスの可否とは別 (issue #5120: ロックセルも選択・コピーは
   * できる。選択可否は cellAt が返すかどうか = グリッド内かつ enabled で決まる)。
   */
  editable: boolean
  /**
   * 現在の**表示単位**の値 (コピーの基準)。
   * 本 hook は表示単位のまま扱い、保存単位への換算はグリッド側が閉じる。
   */
  value: number | null
  /**
   * Escape (focus 時点の値へ戻す) の復元元。`value` と異なり、サーバー値
   * 未設定コマの 0 fallback (AC-S4D-050) を含まない「素の」値
   * (gridRows.ts の `underlyingSlotValue`)。`value` を復元先に使うと、
   * 未編集の (サーバー値未設定な) セルへの Escape が「編集値 0」を書き込み、
   * 保存対象でないセルが dirty 化してサーバー値が意図せず 0 になる。
   */
  originValue: number | null
  /**
   * Escape の復元元の手動変更の印 (非定型の取引の列のみ。他は undefined)。
   * focus 時点の印へ戻すことで、印だけが残った dirty 行を作らない (AC-S4D-060)。
   */
  originMark?: boolean
}

type Props = {
  /** キーボード / clipboard イベントを受ける DOM (セル input を内包する) */
  containerRef: React.RefObject<HTMLElement | null>
  /** 表示行数 (= 表示日数 × 48) */
  rowCount: number
  /** 列数 (= leg 数) */
  colCount: number
  cellAt: (row: number, col: number) => GridCellInfo | undefined
  /** 書き込み可否 (閲覧モードでは false = 書き込み系の操作をすべて無効にする) */
  enabled: boolean
  /**
   * 表示単位のセル入力値を拒否理由付きで解釈する (AC-S4D-028 / AC-S4D-041)。
   * 単位差は呼び出し側が閉じるため、本 hook は単位を知らない。
   */
  parseValue: (raw: string) => CellValueParseResult
  /**
   * 1 操作分の入力拒否レポート。AC-S4D-041 が silent 破棄を禁じているため、
   * 採用しなかった値は必ずここで報告する。拒否ゼロの操作でも呼び、
   * 直前のエラー表示が解除されるようにする。
   */
  onRejections: (report: RawRejectionReport) => void
  onCellEdits: (edits: CellEdit[], coalesceKey?: string) => void
  onUndo: () => void
  onRedo: () => void
}

type Result = {
  /** 現在の矩形選択 (null = 未選択) */
  selection: CellRange | null
  /** グリッドのコンテナに広げるイベントハンドラ (セル input からの委譲を受ける) */
  containerHandlers: {
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void
    onMouseDown: (e: React.MouseEvent<HTMLElement>) => void
    onMouseUp: (e: React.MouseEvent<HTMLElement>) => void
    onDoubleClick: (e: React.MouseEvent<HTMLElement>) => void
    onFocus: (e: React.FocusEvent<HTMLElement>) => void
    onCopy: (e: React.ClipboardEvent<HTMLElement>) => void
    onPaste: (e: React.ClipboardEvent<HTMLElement>) => void
  }
  /** セル input に直接渡す onChange (identity 安定 = GridCell の memo が効く) */
  onCellInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

/** セル input の位置は data 属性から読む (props で座標を配り直さない) */
function readPos(input: HTMLInputElement): CellPos | null {
  const row = Number(input.dataset.row)
  const col = Number(input.dataset.col)
  if (Number.isNaN(row) || Number.isNaN(col)) return null
  return { row, col }
}

function closestCellInput(target: EventTarget | null): HTMLInputElement | null {
  if (!(target instanceof HTMLElement)) return null
  return target.closest<HTMLInputElement>('input[data-row][data-col]')
}

/**
 * セルの値全体を選択状態 (テキスト反転) にする。focus 経路 (クリック / 矢印 /
 * Tab) と、既に focus 済みのセルへの再クリック経路の両方から呼ぶ唯一の窓口
 * (P5 判定をここ 1 箇所に集約する)。
 *
 * conventions.md「Excelライクなグリッド編集」§選択と編集の状態を分ける:
 * クリックは選択のみで値を変えず、そこへ文字を打つと既存値を置き換える
 * (AC-S4D-003、issue #7053。design/supply/48data.html の準備完了モード =
 * `_setMode(MODE_READY)` の `inp.select()` と同じ扱い)。
 *
 * 同節が並べて定める F2 / ダブルクリック (既存値を保持して caret を末尾) 側は
 * placeCaretAtEnd が持つ。
 *
 * P5: ロックセル (!editable) では全選択しない。ロックセルは not-allowed
 * カーソルで「編集できない」ことを示しているのに、フォーカス時にテキストが
 * 反転選択されると「打てば書き換わりそう」に見えてしまい矛盾する
 * (Excel の保護セル = 選択枠のみでテキスト反転はしない)。
 */
function selectCellText(
  input: HTMLInputElement,
  cell: GridCellInfo | undefined,
): void {
  if (cell?.editable !== true) return
  input.select()
}

/**
 * F2 / ダブルクリックで編集に入る窓口。既存値を保持したまま caret を末尾に
 * 置き、続く打鍵を追記にする (conventions.md「Excelライクなグリッド編集」
 * §選択と編集の状態を分ける、AC-S4D-003、#6912)。48 点データのセルは常時
 * input なので編集モードの state は持たず、caret 位置だけで表す
 * (←→ は caret が端にある時だけセル移動する既存の判定がそのまま効く)。
 *
 * P5: ロックセル (!editable) は caret を collapse するだけにする。ダブル
 * クリックの 2 回目の押下で付くネイティブの単語選択を残すと、selectCellText が
 * 避けているテキスト反転が出てしまうため。
 */
function placeCaretAtEnd(
  input: HTMLInputElement,
  cell: GridCellInfo | undefined,
): void {
  if (cell === undefined) return
  const caret = cell.editable ? input.value.length : (input.selectionStart ?? 0)
  input.setSelectionRange(caret, caret)
}

/**
 * 48 点データグリッドの Excel ライクなキーボード操作 (AC-S4D-003) をまとめた hook。
 *
 * キー割当の正本は docs/specs/conventions.md「Excelライクなグリッド編集」:
 *   ↑↓←→        セル移動 (←→ は編集可能セルでは caret が端にある時のみ。
 *                  ロックセルは caret 位置に意味が無いため常にセル移動)
 *   Enter          下へ移動 (Shift+Enter で上)
 *   Tab            右へ移動 (Shift+Tab で左)。行端では次行先頭 / 前行末尾へ折り返す
 *   Shift+矢印     範囲拡張 / Shift+クリック   範囲選択
 *   Ctrl+C         範囲を TSV でコピー
 *   Ctrl+V         Excel の TSV を貼付位置から下/右へスピル
 *   Ctrl+Enter     選択範囲へ一括入力
 *   Delete/Backspace 範囲クリア (単一セルは input の caret 削除に委ねる)
 *   Escape         そのセルの入力を focus 時点の値へ戻す
 *   F2 / ダブルクリック  既存値を保持したまま caret を末尾に置く (以降の打鍵は追記)
 *   Ctrl+Z / Ctrl+Y (Ctrl+Shift+Z)  undo / redo (conventions には無く design 由来)
 *
 * **矢印キーに値の増減 (スピナー) を割り当てない**。Excel ではセル移動が期待挙動で、
 * 無意識の矢印キー操作による誤編集を防ぐため (conventions.md 同節、#4341)。
 *
 * セルに focus が入ると (クリック / 矢印移動 / Tab) 値全体が選択状態になり、そこへ
 * 数字を打つと既存値を置き換える (conventions.md 同節「選択と編集の状態を分ける」、
 * issue #7053)。
 *
 * 移動先はグリッド内の選択可能セルに限る (閲覧モード全体は disabled で
 * そもそも focus できない)。**GC ロック済みセルも選択・移動・Ctrl+C コピーは
 * できる** (issue #5120: 触れないセルでも Excel 同様に選択・コピーはしたい)。
 * 書き込み系 (直接入力・貼付・一括入力・Delete・Escape) だけが GC ロックセルを
 * 常にスキップする — 選択可否 (selectable) と書き込み可否 (editable) は別軸。
 */
export function useSupply48GridKeyboard(props: Props): Result {
  const [selection, setSelection] = useState<CellRange | null>(null)

  // ハンドラの identity を安定させるため、変化する入力は ref 経由で読む
  const propsRef = useRef(props)
  propsRef.current = props
  const selectionRef = useRef<CellRange | null>(selection)
  selectionRef.current = selection

  /** 範囲選択の起点 */
  const anchorRef = useRef<CellPos | null>(null)
  /**
   * focus 中のセルとその focus 時点の値。
   * seq は「同じセルへの入力でも focus し直したら別操作」とするための連番で、
   * 連続キーストロークを 1 undo にまとめる coalesceKey の一部になる。
   */
  const focusRef = useRef<{
    pos: CellPos
    seq: number
    originValue: number | null
    originMark: boolean | undefined
    /**
     * focus 後にこのセルの input へ打鍵による変更 (onCellInputChange) があったか。
     * undo / redo・一括入力・貼付で表示値が変わっても入力途中とは扱わないため、
     * 表示値の比較ではなく打鍵の経路で立てる。
     */
    typedSinceFocus: boolean
  } | null>(null)
  const seqRef = useRef(0)
  /** focus 移動で範囲選択を解除しない (Shift+矢印の拡張中) */
  const keepSelectionOnFocusRef = useRef(false)

  /*
   * 行数・列数が変わる操作 (手動変更フィルタの切替 / 供給源の増減) や編集モードの
   * OFF が入ると、選択座標が別のセルを指す / 編集できないセルに枠だけ残るため選択を
   * 捨てる。座標ベースの範囲操作 (一括入力・範囲クリア) が意図しないセルへ効くのを
   * 防ぐ。ref ではなく state で前回値を持つ (= render が破棄されても取りこぼさない
   * React 公式の「props 変化で state を捨てる」パターン。useSupply48EditSession の
   * dayIndex 監視と同形)。
   */
  const gridKey = `${props.rowCount}:${props.colCount}:${props.enabled}`
  const [prevGridKey, setPrevGridKey] = useState(gridKey)
  if (prevGridKey !== gridKey) {
    setPrevGridKey(gridKey)
    selectionRef.current = null
    anchorRef.current = null
    setSelection(null)
  }

  const applyRange = useCallback((range: CellRange | null) => {
    selectionRef.current = range
    setSelection(range)
  }, [])

  const focusCell = useCallback(
    (pos: CellPos, keepSelection: boolean): void => {
      const { containerRef } = propsRef.current
      const input =
        containerRef.current?.querySelector<HTMLInputElement>(
          `input[data-row="${pos.row}"][data-col="${pos.col}"]`,
        ) ?? null
      if (input === null) return
      keepSelectionOnFocusRef.current = keepSelection
      // セル全体の選択 (テキスト反転) は selectCellText に集約する (issue #7053:
      // マウスクリックはこの関数を通らないため、両方を focus 起点に揃えた)
      input.focus()
      // onFocus は focus() の中で同期的に走る。既に focus 済みのセルを指した等で
      // 発火しなかった場合にフラグが残らないよう倒しておく
      keepSelectionOnFocusRef.current = false
    },
    [],
  )

  /**
   * (dr, dc) 方向へ 1 歩進めた先が選択可能セルか (見つからなければ null)。
   * issue #5120: GC ロック済みセルも選択・移動はできる (書き込み可否は
   * cell.editable が別途持つ)。グリッドは visibleDates × legs の直積で
   * 隙間なく定義される (Supply48DataGrid の cellAt 実装参照) ため、
   * 「1 歩先がグリッド境界内か」だけを見れば足りる (P4: 旧実装は非選択
   * セルを read-major に読み飛ばす走査ループだったが、到達しないコード
   * だった)。閲覧モード (enabled=false) はそもそもどのセルも focus
   * できない (GridCell 側で disabled) が、ここでも同じ条件で塞いで防御する。
   */
  const findNextSelectable = useCallback(
    (from: CellPos, dr: number, dc: number): CellPos | null => {
      const { enabled, rowCount, colCount } = propsRef.current
      if (!enabled) return null
      return stepCell(from, dr, dc, { rowCount, colCount })
    },
    [],
  )

  /**
   * Tab の行端折り返し先を探す (conventions.md「行の右端で Tab を押すと次の行の
   * 先頭セルへ折り返す」)。次行先頭 (Shift+Tab は前行末尾) が選択可能セルか
   * を返す (GC ロック済みセルも対象、issue #5120)。矢印 / Enter は端で
   * 止まるのでここを通らない。findNextSelectable 同様、cellAt がグリッド
   * 範囲内なら必ず値を返すため 1 手先を見るだけで足りる (P4)。
   */
  const findWrapTarget = useCallback(
    (from: CellPos, dc: number): CellPos | null => {
      const { rowCount, colCount, cellAt, enabled } = propsRef.current
      if (!enabled || colCount === 0) return null
      const forward = dc > 0
      const row = from.row + (forward ? 1 : -1)
      const col = forward ? 0 : colCount - 1
      if (row < 0 || row >= rowCount) return null
      return cellAt(row, col) !== undefined ? { row, col } : null
    },
    [],
  )

  /** 範囲内の編集可能セルへ同一値を書き込む (一括入力 / 範囲クリア) */
  const fillRange = useCallback(
    (range: CellRange, value: number | null): void => {
      const { cellAt, onCellEdits } = propsRef.current
      const edits: CellEdit[] = []
      for (const pos of rangeCells(range)) {
        const cell = cellAt(pos.row, pos.col)
        if (cell === undefined || !cell.editable) continue
        edits.push({
          legId: cell.legId,
          date: cell.date,
          koma: cell.koma,
          value,
        })
      }
      if (edits.length > 0) onCellEdits(edits)
    },
    [],
  )

  /** 現在 focus 中セルの coalesceKey (1 セルへの連続入力を 1 undo にまとめる) */
  const coalesceKey = useCallback((pos: CellPos): string | undefined => {
    const focus = focusRef.current
    if (focus === null) return undefined
    if (focus.pos.row !== pos.row || focus.pos.col !== pos.col) return undefined
    return `${pos.row}:${pos.col}:${focus.seq}`
  }, [])

  /**
   * focus 中の単一セルへ編集を 1 件発行する (手入力 / Delete / Escape)。
   * `CellEdit` の形と coalesceKey の与え方をここ 1 箇所に閉じる
   * (範囲への一括書き込みは fillRange が別に持つ)。mark は Escape が渡す focus 時点の手動変更の印
   * (非定型の列で印だけが残らないようにする)。
   */
  const emitCellEdit = useCallback(
    (
      cell: GridCellInfo,
      pos: CellPos,
      value: number | null,
      mark?: boolean,
    ): void => {
      propsRef.current.onCellEdits(
        [
          {
            legId: cell.legId,
            date: cell.date,
            koma: cell.koma,
            value,
            ...(mark === undefined ? {} : { mark }),
          },
        ],
        coalesceKey(pos),
      )
    },
    [coalesceKey],
  )

  // ---------------- イベントハンドラ ----------------

  const onCellInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { cellAt, enabled, parseValue, onRejections } = propsRef.current
      if (!enabled) return
      const pos = readPos(e.currentTarget)
      if (pos === null) return
      const cell = cellAt(pos.row, pos.col)
      if (cell === undefined || !cell.editable) return

      const raw = e.currentTarget.value
      // 全桁削除 (Backspace / テキスト選択削除) はセルを空 (null) にする。
      // 制御コンポーネントのため state を null 更新しないと DOM が旧値へ復元される。
      let value: number | null = null
      if (raw !== '') {
        const parsed = parseValue(raw)
        if (!parsed.ok) {
          /*
           * 制御コンポーネントなので描画上は直前値へ戻るが、AC-S4D-041 は
           * 「値が消えたのに理由が分からない」状態を禁じている。据え置きと
           * 同時に理由を報告し、呼び出し側がエラーとして可視化する。
           */
          onRejections({
            kind: 'cells',
            rejections: [
              { reason: parsed.reason, raw, row: pos.row, col: pos.col },
            ],
          })
          return
        }
        value = parsed.value
      }
      onRejections(NO_REJECTIONS)
      const focus = focusRef.current
      if (focus?.pos.row === pos.row && focus.pos.col === pos.col) {
        focus.typedSinceFocus = true
      }
      emitCellEdit(cell, pos, value)
    },
    [emitCellEdit],
  )

  const onFocus = useCallback((e: React.FocusEvent<HTMLElement>) => {
    const input = closestCellInput(e.target)
    if (input === null) return
    const pos = readPos(input)
    if (pos === null) return

    const cell = propsRef.current.cellAt(pos.row, pos.col)
    seqRef.current += 1
    focusRef.current = {
      pos,
      seq: seqRef.current,
      originValue: cell?.originValue ?? null,
      originMark: cell?.originMark,
      typedSinceFocus: false,
    }

    /*
     * issue #7053: マウスクリックは focusCell を通らないため、ここで select()
     * しないとブラウザネイティブの caret 配置 (クリック位置) がそのまま残り、
     * 打鍵が既存値への追記になっていた (100 のセルに 5 → 1005)。
     * クリック・矢印移動・Tab のどの経路で focus が入っても同じ状態にする。
     */
    selectCellText(input, cell)

    if (keepSelectionOnFocusRef.current) {
      keepSelectionOnFocusRef.current = false
      return
    }
    // 通常の focus 移動 (クリック / Tab / 矢印) は選択 (矩形範囲) を解除して
    // アンカーを置き直す。単一セルの選択枠は GridCell 側のローカル focus
    // 状態で表現する (P3: grid 全体の再 render を避けるため、この state に
    // 単一セル分の 1×1 range を持たせない)。
    anchorRef.current = pos
    selectionRef.current = null
    setSelection(null)
  }, [])

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const input = closestCellInput(e.target)
      if (input === null) return
      const pos = readPos(input)
      if (pos === null) return

      const anchor = anchorRef.current
      if (e.shiftKey && anchor !== null) {
        // Shift+クリック = アンカーからの矩形選択。focus は動かさない
        // (= 一括入力の値の供給元をアンカーセルのままにする)
        e.preventDefault()
        applyRange(makeRange(anchor, pos))
        return
      }
      anchorRef.current = pos
      applyRange(null)
    },
    [applyRange],
  )

  /*
   * 既に focus 済みのセルへの再クリックは focus イベントが再発火しないため、
   * onFocus 側の select() が走らない (issue #7053 の再クリック経路)。
   * ネイティブの caret 配置 (クリック位置に caret を置くだけ) をそのままにすると
   * この経路だけ打鍵が追記に戻ってしまうため、ここで値全体を選択し直す。
   *
   * mousedown ではなく mouseup で行う: mousedown を preventDefault すると
   * ネイティブのテキスト選択の開始ごと止まり、セル内のドラッグ部分選択
   * (onCopy が「編集可能セルは native のテキスト選択コピーに委ねる」前提に
   * している唯一の経路。issue #5120 / P2) と、ダブルクリックの単語選択
   * (onDoubleClick が後から caret 末尾で上書きする) を潰してしまう。
   * 押下〜解放でネイティブの選択が付いた (= collapse していない) 時は
   * 何もしないことで、単純クリックだけを全体選択に寄せる。
   */
  const onMouseUp = useCallback((e: React.MouseEvent<HTMLElement>) => {
    // Shift+クリック (矩形選択) は focus を動かさないので対象外
    if (e.shiftKey) return
    const input = closestCellInput(e.target)
    if (input === null) return
    if (document.activeElement !== input) return
    if (input.selectionStart !== input.selectionEnd) return
    const pos = readPos(input)
    if (pos === null) return
    selectCellText(input, propsRef.current.cellAt(pos.row, pos.col))
  }, [])

  /*
   * ダブルクリックの 2 回目の押下で付いたネイティブの単語選択を、dblclick
   * (2 回目の mouseup の後) で caret 末尾に上書きする。focus 後に打鍵で入力した
   * セルでは何もせず単語選択を残す (入力途中の編集を妨げない。React mock の
   * ExcelGrid が編集中 input 上の dblclick を無視するのと同じ)。
   */
  const onDoubleClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const input = closestCellInput(e.target)
    if (input === null) return
    const pos = readPos(input)
    if (pos === null) return
    const focus = focusRef.current
    if (
      focus !== null &&
      focus.pos.row === pos.row &&
      focus.pos.col === pos.col &&
      focus.typedSinceFocus
    ) {
      return
    }
    placeCaretAtEnd(input, propsRef.current.cellAt(pos.row, pos.col))
  }, [])

  const onCopy = useCallback((e: React.ClipboardEvent<HTMLElement>) => {
    const range = selectionRef.current
    const { cellAt } = propsRef.current

    if (range !== null && isMultiCellRange(range)) {
      const text = rangeToTsv(range, (row, col) => {
        const value = cellAt(row, col)?.value ?? null
        return value === null ? '' : String(value)
      })
      e.preventDefault()
      e.clipboardData.setData('text/plain', text)
      return
    }

    /*
     * 単一セル選択 (issue #5120 / P2): ロックセル (!editable) のみ明示コピーする。
     * 編集可能セルは従来どおり native のテキスト選択コピー (selectCellText の
     * 全体選択、またはセル内のドラッグ部分選択) に委ねる — 常に上書きすると
     * 部分テキスト選択のコピーを潰してしまうため。
     *
     * 発火対象は copy イベントの実際の対象セル (e.target から closestCellInput
     * で解決) に限定し、focusRef の座標は参照しない。container は thead も
     * 内包するため、focusRef 基準だと成約 ID 等のヘッダテキストを選択して
     * Ctrl+C しても直前 focus セルの値が誤って上書きされてしまう (P2)。
     */
    const input = closestCellInput(e.target)
    if (input === null) return
    const pos = readPos(input)
    if (pos === null) return
    const targetCell = cellAt(pos.row, pos.col)
    if (targetCell === undefined || targetCell.editable) return

    e.preventDefault()
    e.clipboardData.setData(
      'text/plain',
      targetCell.value === null ? '' : String(targetCell.value),
    )
  }, [])

  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLElement>) => {
      const {
        enabled,
        cellAt,
        onCellEdits,
        rowCount,
        colCount,
        parseValue,
        onRejections,
      } = propsRef.current
      if (!enabled) return
      const input = closestCellInput(e.target)
      if (input === null) return
      const pos = readPos(input)
      if (pos === null) return

      const text = e.clipboardData.getData('text')
      if (text === '') return
      const plan = planPaste(parseTsv(text), pos, selectionRef.current, {
        rowCount,
        colCount,
      })
      // 単一セルへの単一値貼付は従来どおり input のネイティブ貼付に委ねる。
      if (plan.isSingleValue && !plan.fillWholeSelection) return
      e.preventDefault()

      const edits: CellEdit[] = []
      /*
       * AC-S4D-042: 貼付範囲の「数値でないセル (空欄・文字列等)」は反映せず元の値を
       * 残し、件数をまとめて通知する。空欄も「数値でないセル」なので件数に数える
       * (既存値のクリアにも使わせない)。GC ロック・グリッド外は「数値以外だから
       * 落ちた」わけではないので、この件数には含めない。
       *
       * グリッドに収まる範囲へ先に切り詰める点 (Excel で列全体〈100 万行〉をコピー
       * した状態で貼られても、書けないセルを数百万回走査してタブを固めない) は
       * planPaste がグリッド内の行だけを見て targetRange を決めることで満たされる。
       */
      let skippedCount = 0
      for (const target of rangeCells(plan.targetRange)) {
        const raw = plan.sourceAt(target.row, target.col)
        if (raw === undefined) continue
        const cell = cellAt(target.row, target.col)
        // GC ロック / 範囲外はスキップ (貼付元の形は保ったまま書ける所だけ書く)
        if (cell === undefined || !cell.editable) continue
        if (raw.trim() === '') {
          skippedCount += 1
          continue
        }
        const parsedValue = parseValue(raw)
        if (!parsedValue.ok) {
          skippedCount += 1
          continue
        }
        edits.push({
          legId: cell.legId,
          date: cell.date,
          koma: cell.koma,
          value: parsedValue.value,
        })
      }
      onRejections(
        skippedCount > 0
          ? { kind: 'pasteSkipped', count: skippedCount }
          : NO_REJECTIONS,
      )
      if (edits.length > 0) onCellEdits(edits)

      anchorRef.current = pos
      applyRange(plan.targetRange)
    },
    [applyRange],
  )

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      const {
        enabled,
        cellAt,
        onUndo,
        onRedo,
        parseValue,
        onRejections,
        rowCount,
        colCount,
      } = propsRef.current
      const input = closestCellInput(e.target)
      if (input === null) return
      const pos = readPos(input)
      if (pos === null) return
      const cell = cellAt(pos.row, pos.col)
      if (cell === undefined) return

      const range = selectionRef.current
      const hasRange = range !== null && isMultiCellRange(range)

      // ---- Ctrl / Cmd 併用 ----
      if (e.ctrlKey || e.metaKey) {
        const key = e.key.toLowerCase()
        // undo / redo も書き込み操作なので閲覧モード・確定済みでは効かせない
        // (セル input の disabled 頼みにせず、他の書き込み経路と同じ明示 guard を置く)
        if (key === 'z' && !e.shiftKey) {
          if (!enabled) return
          e.preventDefault()
          onUndo()
          return
        }
        if (key === 'y' || (key === 'z' && e.shiftKey)) {
          if (!enabled) return
          e.preventDefault()
          onRedo()
          return
        }
        // Ctrl+Enter: 選択範囲へ一括入力 (値は focus 中セルの入力値)
        if (isEnterKey(e)) {
          /*
           * P1 (issue #5120): アクティブ (focus 中) セルがロック等で書き込み
           * 不可なら no-op にする。issue #5120 でロックセルも選択・移動できる
           * ようになった結果、Shift+矢印の範囲拡張はアクティブセルをロック
           * セル上へ止められるようになった。その状態のまま Ctrl+Enter を通すと
           * 打鍵なしに GC 確定値 (ロックセルの表示値) が範囲内へ伝播してしまう
           * (Excel の保護セルでは打鍵→fill 自体ができないのと等価にする)。
           */
          if (!enabled || !hasRange || !cell.editable) return
          e.preventDefault()
          /*
           * 空セルでの一括入力は何もしない (旧実装と同じ no-op)。
           * 範囲クリアは Delete が担うので、ここで fillRange(range, null) を
           * 足すと「48 コマ × 全供給源を 1 打鍵で消す」経路が二重になる。
           * ただし空を不正値として報告はしない (= 理由の無いエラーを出さない)。
           */
          if (input.value === '') {
            onRejections(NO_REJECTIONS)
            return
          }
          /*
           * セルは制御コンポーネントで、採用しなかった入力は直前値へ戻る。
           * つまり input.value は「空」か「採用済みの数値」しか取らず、この
           * 拒否分岐は通常経路では到達しない (= defense in depth)。到達した
           * 場合でも silent に捨てず理由を報告する (AC-S4D-041)。
           */
          const parsed = parseValue(input.value)
          if (!parsed.ok) {
            onRejections({
              kind: 'cells',
              rejections: [
                {
                  reason: parsed.reason,
                  raw: input.value,
                  row: pos.row,
                  col: pos.col,
                },
              ],
            })
            return
          }
          onRejections(NO_REJECTIONS)
          fillRange(range, parsed.value)
        }
        // Ctrl+C は onCopy (clipboard イベント) が処理する
        return
      }
      if (e.altKey) return

      /*
       * 全選択中のセルへ、表示中の値と同じ 1 文字を打った場合の救済 (issue #7053
       * self-review 指摘): クリック選択 (onFocus / onMouseUp の input.select())
       * は打鍵を「既存値の置き換え」にするが、置き換え後の文字列が置き換え前と
       * 完全一致すると React の controlled input は value 不変の input イベントを
       * onChange まで通さない (facebook/react#11488。tracked value と実際の
       * value が同じままのため「変化なし」と判定される)。
       * サーバー値未設定セル (0 表示 fallback) へ明示的に「0」を打つ操作
       * (#7689 の回帰テスト) がまさにこの経路で、ネイティブ入力に委ねると
       * onCellInputChange が一度も呼ばれず dirty 化されない。
       * 全選択 (selectionStart=0 かつ selectionEnd=末尾) かつ入力文字が現在値と
       * 一致する (= 置き換え後も同じ文字列になる) 場合だけ、ここで編集を明示的に
       * 記録して補う。それ以外 (複数桁 / 異なる文字) は通常どおりネイティブ input
       * イベント (onCellInputChange) に委ねる。
       *
       * **preventDefault しない** (レビュー指摘 #7737): 打鍵を握り潰すと全体選択
       * (0..length) が解除されないまま次の打鍵を迎え、表示値 1 桁のセル `4` に
       * 「4500」と打つと 2 打鍵目が全体を置き換えて `500` になる
       * (AC-S4D-003「置き換わるのは初回打鍵だけ、以降は追記」違反)。
       * ネイティブの置き換えに委ねれば同じ文字列に置き換わって caret が末尾へ
       * 収束するだけで、この分岐が補う onChange 欠落はそのまま残る
       * (= 二重記録にはならない。仮に将来 React が onChange を通すようになっても
       * 同じ coalesceKey・同じ値なので undo 1 件に畳まれる)。
       */
      if (
        enabled &&
        cell.editable &&
        e.key.length === 1 &&
        input.selectionStart === 0 &&
        input.selectionEnd === input.value.length &&
        input.value === e.key
      ) {
        const parsed = parseValue(e.key)
        if (parsed.ok) {
          onRejections(NO_REJECTIONS)
          emitCellEdit(cell, pos, parsed.value)
          return
        }
      }

      if (e.key === 'F2') {
        e.preventDefault()
        placeCaretAtEnd(input, cell)
        return
      }

      // ---- クリア ----
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (!enabled) return
        if (hasRange) {
          e.preventDefault()
          fillRange(range, null)
          return
        }
        if (e.key === 'Delete' && cell.editable) {
          e.preventDefault()
          emitCellEdit(cell, pos, null)
        }
        // Backspace の単一セルは input の caret 削除に委ねる (onChange が空を拾う)
        return
      }

      // ---- Escape: focus 時点の値へ戻す ----
      if (e.key === 'Escape') {
        if (!enabled || !cell.editable) return
        const focus = focusRef.current
        if (focus === null) return
        e.preventDefault()
        // 入力時と同じ coalesceKey で戻す = 履歴は「入力前後で差分なし」になり畳まれる
        emitCellEdit(cell, pos, focus.originValue, focus.originMark)
        // focus 時点の値へ戻したので入力途中ではない (dblclick が caret 末尾に戻る)
        focus.typedSinceFocus = false
        input.select()
        return
      }

      // ---- 移動 / 範囲拡張 ----
      // conventions.md「Excelライクなグリッド編集」: 矢印キーはセル移動のみで、
      // 値の増減 (スピナー) には割り当てない (#4341)。
      let dr = 0
      let dc = 0
      if (e.key === 'ArrowUp') dr = -1
      else if (e.key === 'ArrowDown') dr = 1
      else if (isEnterKey(e)) dr = e.shiftKey ? -1 : 1
      else if (e.key === 'Tab') dc = e.shiftKey ? -1 : 1
      else if (e.key === 'ArrowLeft') dc = -1
      else if (e.key === 'ArrowRight') dc = 1
      else return

      // Shift+矢印 = 範囲拡張 (Tab / Enter は通常移動のまま)
      if (
        e.shiftKey &&
        (e.key === 'ArrowUp' ||
          e.key === 'ArrowDown' ||
          e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight')
      ) {
        const target = stepCell(pos, dr, dc, { rowCount, colCount })
        if (target === null) return
        e.preventDefault()
        const anchor = anchorRef.current ?? pos
        anchorRef.current = anchor
        applyRange(makeRange(anchor, target))
        focusCell(target, true)
        return
      }

      // ←→ は caret がセル端にある時だけ移動 (それ以外は input 内の caret 移動)。
      // ロックセル (!editable) は selectCellText が select() を呼ばない (P5) ため
      // caret が片端に collapse しており、caret 判定に委ねると片方向の矢印が
      // readOnly input 内の caret 移動に消費される (値の桁数分だけ空打ちが要る)。
      // 編集できないセルでは caret 位置に意味が無いので常にセル移動として扱う。
      if (cell.editable) {
        if (e.key === 'ArrowLeft' && input.selectionStart !== 0) return
        if (
          e.key === 'ArrowRight' &&
          input.selectionEnd !== input.value.length
        ) {
          return
        }
      }

      const next = findNextSelectable(pos, dr, dc)
      if (next !== null) {
        e.preventDefault()
        focusCell(next, false)
        return
      }

      // 行端の Tab は次行先頭 (Shift+Tab は前行末尾) へ折り返す。
      // 矢印 / Enter は conventions 上の折り返し規定が無いので端で止まる。
      if (e.key === 'Tab') {
        const wrapped = findWrapTarget(pos, dc)
        if (wrapped === null) return
        e.preventDefault()
        focusCell(wrapped, false)
      }
    },
    [
      emitCellEdit,
      fillRange,
      findNextSelectable,
      findWrapTarget,
      focusCell,
      applyRange,
    ],
  )

  return {
    selection,
    containerHandlers: {
      onKeyDown,
      onMouseDown,
      onMouseUp,
      onDoubleClick,
      onFocus,
      onCopy,
      onPaste,
    },
    onCellInputChange,
  }
}
